import { useState, useEffect } from 'react'
import { Plus, Edit, Trash2, Search, Package } from 'lucide-react'
import { toast } from 'sonner'
import z from 'zod'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'

// Componentes UI Globales
import { Button } from '../components/ui/button'
import { Input } from '../components/ui/input'
import { FormError } from '../components/ui/FormError'
import { DiscreteLoader } from '../components/ui/discrete-loader'
import { GlobalPaginator } from '../components/ui/global-paginator'
import { TableSkeleton } from '../components/ui/table-skeleton'
import { ConfirmationModal } from '../components/ui/ConfirmationModal'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from '../components/ui/dialog'

// Cliente HTTP y Servicio
import { productTypeService } from '../services/product-type.service'

// ─── 1. Tipos de Datos (Interfaces) ──────────────────────────────────────────
export interface ProductTypeItem {
  id: number
  name: string
  code: string
  description: string
  requires_lot: number
}

// ─── 2. Esquema de Validación (Zod) ──────────────────────────────────────────
const formSchema = z.object({
  name: z.string().min(1, 'El nombre es obligatorio'),
  code: z.string().min(1, 'El código es obligatorio'),
  description: z.string().optional(),
  requires_lot: z.boolean(),
})

type FormValidate = z.infer<typeof formSchema>

// ─── 3. Componente Principal ───────────────────────────────────────────────
export function ProductTypes() {
  // ─── Estados (Variables Reactivas) ───
  const [data, setData] = useState<ProductTypeItem[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')
  const [refreshCount, setRefreshCount] = useState(0)

  // Control de Modales
  const [showModal, setShowModal] = useState(false)
  const [editingId, setEditingId] = useState<number | null>(null)
  const [deleteConfirmId, setDeleteConfirmId] = useState<number | null>(null)

  // Paginación
  const [currentPage, setCurrentPage] = useState(1)
  const itemsPerPage = 10

  // ─── Formulario (React Hook Form) ───
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting }
  } = useForm<FormValidate>({
    resolver: zodResolver(formSchema),
    defaultValues: { name: '', code: '', description: '', requires_lot: false }
  })

  // ─── Efecto: Cargar Datos de la API ───
  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true)
      try {
        const response = await productTypeService.getProductTypes()
        const items = response.data?.data || response.data || response || []
        setData(items)
      } catch (error) {
        toast.error('Error al cargar los datos')
      } finally {
        setIsLoading(false)
      }
    }
    fetchData()
  }, [refreshCount])

  // ─── Lógica de Paginación y Búsqueda ───
  const filteredData = data.filter(item =>
    item.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    item.code?.toLowerCase().includes(searchQuery.toLowerCase())
  )

  const startIndex = (currentPage - 1) * itemsPerPage
  const paginatedData = filteredData.slice(startIndex, startIndex + itemsPerPage)

  // ─── Funciones de Interfaz ───
  const openCreateModal = () => {
    setEditingId(null)
    reset({ name: '', code: '', description: '', requires_lot: false })
    setShowModal(true)
  }

  const openEditModal = (item: ProductTypeItem) => {
    setEditingId(item.id)
    reset({
      name: item.name,
      code: item.code,
      description: item.description || '',
      requires_lot: Number(item.requires_lot) === 1
    })
    setShowModal(true)
  }

  // ─── Funciones API (Guardar y Eliminar) ───
  const onSubmit = async (formData: FormValidate) => {
    const payload = {
      ...formData,
      requires_lot: formData.requires_lot ? 1 : 0
    }

    try {
      if (editingId) {
        await productTypeService.updateProductType(editingId, payload)
        toast.success('Tipo de producto actualizado exitosamente')
      } else {
        await productTypeService.createProductType(payload)
        toast.success('Tipo de producto creado exitosamente')
      }
      setShowModal(false)
      setRefreshCount(prev => prev + 1)
    } catch (error) {
      toast.error('Error al guardar el tipo de producto')
    }
  }

  const executeDelete = async () => {
    if (!deleteConfirmId) return
    try {
      await productTypeService.deleteProductType(deleteConfirmId)
      toast.success('Tipo de producto eliminado')
      setRefreshCount(prev => prev + 1)
    } catch (error) {
      toast.error('Error al eliminar')
    } finally {
      setDeleteConfirmId(null)
    }
  }

  // ─── Renderizado (La Vista HTML JSX) ─────────────────────────────────────
  return (
    <div className="space-y-6 p-6">

      {/* 1. Header y Buscador */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Tipos de Producto</h1>
          <p className="mt-1 text-sm text-slate-600">Administra los tipos de producto del sistema.</p>
        </div>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <Input
              placeholder="Buscar..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-9 sm:w-64"
            />
          </div>
          <Button onClick={openCreateModal} className="bg-[#3B7D3B] hover:bg-[#2d6a2d]">
            <Plus className="mr-2 h-4 w-4" /> Nuevo Tipo
          </Button>
        </div>
      </div>

      {/* 2. Tabla de Datos */}
      {isLoading ? (
        <TableSkeleton
          headers={['Nombre', 'Código', 'Descripción', 'Maneja Lote', 'Acciones']}
          columns={['text', 'text', 'text', 'badge', 'actions']}
          rows={5}
        />
      ) : (
        <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full whitespace-nowrap">
              <thead className="border-b border-slate-200 bg-slate-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-slate-500">Nombre</th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-slate-500">Código</th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-slate-500">Descripción</th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-slate-500">Maneja Lote</th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-slate-500">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {paginatedData.length > 0 ? (
                  paginatedData.map((item) => (
                    <tr key={item.id} className="hover:bg-slate-50">
                      <td className="px-6 py-4 font-medium text-slate-900">{item.name}</td>
                      <td className="px-6 py-4 text-slate-700">{item.code}</td>
                      <td className="px-6 py-4 text-slate-700">{item.description}</td>
                      <td className="px-6 py-4 text-slate-700">
                        {Number(item.requires_lot) === 1 ? (
                          <span className="inline-flex items-center rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-800">Sí</span>
                        ) : (
                          <span className="inline-flex items-center rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-medium text-slate-800">No</span>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex gap-2">
                          <button onClick={() => openEditModal(item)} className="rounded p-1 hover:bg-slate-100" title="Editar">
                            <Edit className="h-4 w-4 text-slate-600" />
                          </button>
                          <button onClick={() => setDeleteConfirmId(item.id)} className="rounded p-1 hover:bg-red-50" title="Eliminar">
                            <Trash2 className="h-4 w-4 text-red-500" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={4} className="px-6 py-12 text-center">
                      <Package className="mx-auto mb-3 h-8 w-8 text-slate-300" />
                      <p className="text-slate-500">No se encontraron registros</p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* 3. Paginación */}
          {filteredData.length > 0 && (
            <GlobalPaginator
              totalItems={filteredData.length}
              itemsPerPage={itemsPerPage}
              currentPage={currentPage}
              onPageChange={setCurrentPage}
            />
          )}
        </div>
      )}

      {/* 4. Modal Crear/Editar */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{editingId ? 'Editar Tipo de Producto' : 'Nuevo Tipo de Producto'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4 py-4">

            {/* Input Nombre */}
            <div>
              <label className="mb-1 block text-sm font-medium text-slate-700">Nombre *</label>
              <Input {...register('name')} placeholder="Ingresa el nombre" />
              {errors.name && <FormError message={errors.name.message} />}
            </div>

            {/* Input Código */}
            <div>
              <label className="mb-1 block text-sm font-medium text-slate-700">Código *</label>
              <Input {...register('code')} placeholder="Ej. GRUPO_IV" />
              {errors.code && <FormError message={errors.code.message} />}
            </div>

            {/* Input Descripción */}
            <div>
              <label className="mb-1 block text-sm font-medium text-slate-700">Descripción</label>
              <Input {...register('description')} placeholder="Breve descripción" />
              {errors.description && <FormError message={errors.description.message} />}
            </div>

            {/* Checkbox Lote */}
            <div className="flex items-center gap-2 mt-2">
              <input
                type="checkbox"
                id="requires_lot"
                {...register('requires_lot')}
                className="h-4 w-4 rounded border-slate-300 text-[#3B7D3B] focus:ring-[#3B7D3B]"
              />
              <label htmlFor="requires_lot" className="text-sm font-medium text-slate-700">
                Requiere Lote
              </label>
            </div>
            {errors.requires_lot && <FormError message={errors.requires_lot?.message} />}

            <DialogFooter className="mt-6">
              <Button type="button" variant="outline" onClick={() => setShowModal(false)} disabled={isSubmitting}>
                Cancelar
              </Button>
              <Button type="submit" disabled={isSubmitting} className="bg-[#3B7D3B] hover:bg-[#2d6a2d]">
                {isSubmitting ? <DiscreteLoader /> : 'Guardar'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* 5. Modal Global de Confirmación para Eliminar */}
      <ConfirmationModal
        isOpen={deleteConfirmId !== null}
        onClose={() => setDeleteConfirmId(null)}
        onConfirm={executeDelete}
        preset="delete"
        itemName="tipo de producto"
      />
    </div>
  )
}
